import { createReduxOperation, createActionWithPayload, makeConstants } from "src/store/utils";
import { parseJson, getTimeFromTimestamp, safeGet, TimeDifferenceGA } from "src/shared/utils";
import AllOrgMetricsData, { IChartReqData } from "src/shared/models/AllOrgMetricsModel";
import { ThunkResult } from "src/shared/types";
import { getAllOrgs } from "src/api/allOrg";
import { getCharts } from "src/api/common";
import { FetchAllOrgMetricsPayload } from "./types";
import { getXClientIdHeader, ServiceName } from "src/utils/getservicename";
import { validateAccess } from "../login";

// Generate constants, actions and reducer

const {
  actions: [allOrgMetricsRequest, allOrgMetricsSuccess, allOrgMetricsFailure, allOrgMetricsClear],
  reducer: allOrgMetricsReducer,
} = createReduxOperation("ALL_ORG_METRICS");

const AllOrgParentConstants = makeConstants(["FETCHING_ALL_ORG_DATA"]);

const toggleChartsDataPending = createActionWithPayload<symbol, boolean>(
  AllOrgParentConstants.get("FETCHING_ALL_ORG_DATA") as symbol
);

// handle thunk

function fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const shouldFetchFreshData = payload?.fresh;
    dispatch(allOrgMetricsRequest());
    dispatch(toggleChartsDataPending(true));
    const timeTab = getState().app.selectedTab;
    const { response: allOrgsMetricsResponse, error: allOrgsMetricsError } = await getAllOrgs({
      timeTab,
      shouldFetchFreshData,
    });

    if (allOrgsMetricsResponse) {
      const data = {
        categorialData: allOrgsMetricsResponse,
      };
      const filterConfig = getState().app.appConfig.payload || null;
      const userRoles = getState().app.userRoles.payload;

      if (!validateAccess(userRoles)) {
        dispatch(toggleChartsDataPending(false));
        return;
      }
      const allOrgMetricsData = AllOrgMetricsData.createAllOrgMetrics(data);

      //prepare chart data and call API
      let chartData: IChartReqData[] = allOrgMetricsData.prepareChartData(timeTab, filterConfig, userRoles.cities);
      const lastUpdatedTimeStamp = safeGet(getState(), "home.allOrgMetrics.metrics.payload.lastUpdatedTimeStamp", null);
      allOrgMetricsData["lastUpdatedTimeStamp"] = lastUpdatedTimeStamp;
      dispatch(allOrgMetricsSuccess(allOrgMetricsData));
      const { response: chartsResponse, error: chartsError } = await getCharts({
        data: JSON.stringify(chartData),
        timeTab,
        shouldFetchFreshData,
      });
      if (chartsResponse && chartsResponse.data) {
        chartsResponse.data.forEach((curr, index) => {
          curr.data = parseJson(curr.data);
          curr.isHidden = chartData[index].isHidden;
        });
        allOrgMetricsData.setNumbers(chartsResponse.data, timeTab);
        allOrgMetricsData["lastUpdatedTimeStamp"] = getTimeFromTimestamp(chartsResponse.lastUpdatedTimeStamp);

        // Prevent some earlier tab data from replacing redux store value of new tab data due to slow response (Race condition)
        if (getState().app.selectedTab !== timeTab) {
          dispatch(toggleChartsDataPending(false));
          return;
        }
        dispatch(allOrgMetricsSuccess(allOrgMetricsData));
        if (timeTab == "Today") {
          const currentChartsData = chartsResponse.data.filter((item) => {
            return item["time_view"] == "currentChart";
          });
          TimeDifferenceGA(currentChartsData, "All Org Metrics Page");
        }
      } else {
        dispatch(allOrgMetricsFailure(chartsError));
      }
      dispatch(toggleChartsDataPending(false));
    } else {
      dispatch(allOrgMetricsFailure(allOrgsMetricsError));
    }
  };
}

export { allOrgMetricsReducer, AllOrgParentConstants, allOrgMetricsClear as clearAllOrgMetrics, fetchAllOrgMetrics };
